// src/pages/Community.js
import React, { useState, useEffect } from "react";
import { Outlet, useNavigate, useMatch } from "react-router-dom";
import "./css/Community.css";
import GTASidebar from "./Community/components/GTASidebar";
import GeneralDiscussion from "./Community/GeneralDiscussion";
import MissionsIndex from "./Community/MissionsIndex";
import StoryCharactersIndex from "./Community/StoryCharactersIndex";
import LocationIndex from "./Community/LocationIndex";
import WeponsVehicleIndex from "./Community/WeponsVehicleIndex";
import TipsTricksIndex from "./Community/TipsTricksIndex";
import MusicRadioIndex from "./Community/MusicRadioIndex";
import Achievements from "./Community/Achievements";
import News_Updates from "./Community/News_UpdatesIndex";
import { db } from "../firebase";
import {
  collection,
  getDocs,
  query as fsQuery,
  where,
  orderBy,
} from "firebase/firestore";

/** Used only for the embedded section on Home */
function EmbeddedCategoryFeed({ categoryKey, title }) {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    (async () => {
      setLoading(true);
      try {
        if (categoryKey === "missions") {
          // Show official missions in embedded mode
          const qy = fsQuery(
            collection(db, "missions"),
            orderBy("createdAt", "desc")
          );
          const snap = await getDocs(qy);
          if (!mounted) return;
          setPosts(
            snap.docs.map((d) => ({ id: d.id, ...d.data(), __official: true }))
          );
          return;
        }

        // Other categories = community posts
        const qy = fsQuery(
          collection(db, "community_posts"),
          where("category", "==", categoryKey)
        );
        const snap = await getDocs(qy);
        if (!mounted) return;
        const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        list.sort(
          (a, b) =>
            (b.timestamp?.toMillis?.() || 0) - (a.timestamp?.toMillis?.() || 0)
        );
        setPosts(list);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [categoryKey]);

  return (
    <div className="embedded-category">
      <div className="category-header">
        <h2 className="category-title">{title}</h2>
      </div>

      {loading ? (
        <div className="loading">Loading…</div>
      ) : posts.length === 0 ? (
        <div className="empty">
          No posts yet in <strong>{title}</strong>.
        </div>
      ) : (
        <ul className="category-post-list">
          {posts.map((p) => (
            <li
              key={p.id}
              className={`category-post ${p.__official ? "is-official" : ""}`}
            >
              {p.__official ? (
                <a
                  href={`/community/missions/${p.id}`}
                  className="category-link"
                >
                  <div className="post-title">{p.missionName}</div>
                  <div className="post-meta">
                    {p.createdAt?.toDate?.()?.toLocaleString() || "Just now"}
                  </div>
                </a>
              ) : (
                <>
                  <div className="post-title">
                    {p.title || (p.content ? p.content.slice(0, 120) : "Untitled")}
                  </div>
                  <div className="post-meta">
                    {p.type?.toUpperCase?.()} •{" "}
                    {p.timestamp?.toDate?.()
                      ? p.timestamp.toDate().toLocaleString()
                      : "Just now"}
                  </div>
                </>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default function CommunityLayout({
  embedded = false,
  defaultKey = "general",
  user,
  setShowAuthModal,
}) {
  const navigate = useNavigate();

  // Routed mode: pick active from URL
  const forumMatch = useMatch("/forum");
  const missionsMatch = useMatch("/missions/*");
  const storyCharsMatch = useMatch("/story-characters/*");
  const locationsMatch = useMatch("/map-locations/*");

  const vehiclesWeaponsMatch = useMatch("/vehicles-weapons/*");
  const vehicleDetailMatch = useMatch("/vehicles/*");
  const weaponDetailMatch = useMatch("/weapons/*");

  const tipsMatch = useMatch("/tips-tricks/*");
  const musicMatch = useMatch("/music-radio/*");
  const achievementsMatch = useMatch("/achievements/*");
  const newsMatch = useMatch("/news-updates/*");
  const vehiclesMatch = vehiclesWeaponsMatch || vehicleDetailMatch || weaponDetailMatch;
  const activeFromPath =
    forumMatch ? "general" :
      missionsMatch ? "missions" :
        storyCharsMatch ? "story-characters" :
          locationsMatch ? "map_locations" :
            vehiclesMatch ? "vehicles_weapons" :
              tipsMatch ? "tips_tricks" :
                musicMatch ? "music_radio" :
                  achievementsMatch ? "achievements" :
                    newsMatch ? "news_updates" :
                      "general";


  // Embedded mode: local state
  const [active, setActive] = useState(defaultKey);
  const activeKey = embedded ? active : activeFromPath;

  // Replace your existing handleSelect with this:
  const handleSelect = React.useCallback(
    (key) => {
      if (embedded) {
        setActive(key);
        return;
      }

      const routeMap = {
        general: "/forum",
        missions: "/missions",
        "story-characters": "/story-characters",
        map_locations: "/map-locations",
        vehicles_weapons: "/vehicles-weapons",
        tips_tricks: "/tips-tricks",
        music_radio: "/music-radio",
        achievements: "/achievements",
        news_updates: "/news-updates",
      };

      navigate(routeMap[key] || "/forum", { replace: false });
    },
    [embedded, navigate]
  );

  function renderRightPane() {
    if (!embedded) return <Outlet />;

    switch (activeKey) {
      case "general":
        return <GeneralDiscussion user={user} setShowAuthModal={setShowAuthModal} />;
      case "missions":
        return <MissionsIndex user={user} />;
      case "story-characters":
        return <StoryCharactersIndex user={user} />;
      case "map_locations":
        return <LocationIndex user={user} />;
      case "vehicles_weapons":
        return <WeponsVehicleIndex />;
      case "tips_tricks":
        return <TipsTricksIndex />;
      case "music_radio":
        return <MusicRadioIndex />;
      case "achievements":
        return <Achievements user={user} />;
      case "news_updates":
        return <News_Updates />;
      default:
        return <EmbeddedCategoryFeed categoryKey={activeKey} title="Feed" />;
    }
  }

  return (
    <div className={`community-layout ${embedded ? "" : "not-embedded"}`}>
      <GTASidebar active={activeKey} onSelect={handleSelect} />
      <main className="community-main">
        {embedded
          ? renderRightPane()
          : (
            <Outlet context={{ user, setShowAuthModal }} />
          )}
      </main>
    </div>
  );
}
